import { GOOGLE_MAP_KEY } from "consts/env";

/**
 * Opciones compartidas del loader de Google Maps.
 *
 * Van en un modulo aparte para que tengan identidad estable: @react-google-maps/api compara
 * `libraries` por referencia y, si cambia, avisa que se recargo el script sin necesidad.
 * Ademas asi todos los mapas del panel usan el mismo id y el script se carga una sola vez.
 */
export const GOOGLE_MAPS_LIBRARIES = ["places"];

export const GOOGLE_MAPS_LOADER_OPTIONS = {
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAP_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
    // Sin esto la API responde en ingles ("Rio Negro Province", "General Roca Department") y
    // los nombres nunca coinciden con los de la lista de localidades del back. region sesga
    // los resultados a Argentina.
    //
    // Va aca y no en cada mapa porque el script se carga UNA sola vez por sesion: si el
    // primer mapa que se monta no pide español, toda la sesion queda en ingles aunque los
    // demas si lo pidan.
    language: "es",
    region: "AR",
};

/**
 * Centro por defecto de los mapas: Viedma, capital de Rio Negro.
 *
 * Se usa cuando todavia no hay coordenadas cargadas. Estaba hardcodeado y distinto en cada
 * mapa (Viedma en los modales de establecimientos, Rosario en el ModalMapa, CABA en los dos
 * Mapa.jsx), de ahi que el pin apareciera en el medio de la nada segun que componente se
 * montara primero.
 */
export const CENTRO_POR_DEFECTO = {
    lat: -40.813326,
    lng: -62.997741,
};

/**
 * Busca un tipo de address_component recorriendo TODOS los results, no solo el primero.
 * Google los devuelve del mas preciso al mas general, asi que el primero que tenga el dato
 * es el mejor disponible.
 */
const buscarEnResults = (results, type, campo = "long_name") => {
    for (const result of results) {
        const value = result?.address_components?.find((c) =>
            c.types.includes(type)
        )?.[campo];
        if (value) return value;
    }
    return "";
};

/**
 * Tipos que pueden contener lo que el back llama "localidad", en orden de prioridad.
 *
 * No se elige uno solo: se devuelven TODOS los que vengan, y quien matchea contra la lista
 * del back prueba en orden hasta encontrar uno. Antes se elegia con || y ganaba locality por
 * ser el primero no vacio, asi que cuando el dato bueno estaba en sublocality o en el
 * departamento nunca se usaba.
 */
const TIPOS_LOCALIDAD = [
    "locality",
    "sublocality",
    "sublocality_level_1",
    "administrative_area_level_2",
];

/**
 * Google a veces devuelve el street_number con el CPA pegado ("754 R8500"). Solo interesa
 * el primer token: ademas de ser el dato correcto, el campo del formulario valida max 10.
 */
const limpiarNumero = (valor) => (valor || "").trim().split(/\s+/)[0] ?? "";

/**
 * Extrae calle, numero, localidad y CP de una respuesta de geocoding.
 *
 * Recibe el ARRAY completo de results a proposito. En el geocoding inverso (arrastrar el pin)
 * results[0] suele ser de nivel "route" cuando el punto no cae justo sobre una direccion, y
 * ese nivel NO trae street_number aunque si trae route y postal_code. Por eso leer solo
 * results[0] hacia que la calle y el CP se cargaran bien y el numero no.
 */
export const parseDireccion = (results = []) => {
    const localidadCandidatas = TIPOS_LOCALIDAD.map((tipo) =>
        buscarEnResults(results, tipo)
    )
        .filter(Boolean)
        .filter((valor, i, arr) => arr.indexOf(valor) === i);

    return {
        calle: buscarEnResults(results, "route"),
        numero: limpiarNumero(buscarEnResults(results, "street_number")),
        // La primera candidata es la que se muestra en el texto de la direccion.
        localidad: localidadCandidatas[0] ?? "",
        localidadCandidatas,
        // administrative_area_level_1 es la provincia en Argentina.
        provincia: buscarEnResults(results, "administrative_area_level_1"),
        // En espanol el long_name viene como "Provincia de Rio Negro", mientras el back
        // guarda "Rio Negro". El short_name da otra variante mas y el matcheo prueba las dos.
        provinciaCorta: buscarEnResults(
            results,
            "administrative_area_level_1",
            "short_name"
        ),
        cod: buscarEnResults(results, "postal_code"),
    };
};

/**
 * Arma el texto de una direccion salteando las partes vacias.
 *
 * Antes se concatenaba a mano con templates, y cuando no habia calle ni numero quedaba un
 * texto arrancando en coma (", General Roca Department").
 */
export const formatDireccion = ({
    calle,
    numero,
    localidad,
    provincia,
    pais,
} = {}) => {
    const calleYNumero = [calle, numero].filter(Boolean).join(" ");
    return [calleYNumero, localidad, provincia, pais].filter(Boolean).join(", ");
};
